import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BodyText,
  Button,
  FlexRow,
  Grid,
  IconType,
  ISelectOption,
  NumberField,
  RotatingIcon,
  SelectField,
  TextField,
} from 'ui-toolkit';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  eACHDisbursementCategories,
  achDisbursementCategoriesOptions,
} from '../../../../../types/order/escrow/eEscrowAchPushDisbursementCategories';
import { IAchPushDisbursementData } from '../../../../../types/order/escrow/IAchPushDisbursementData';
import { accountTypeOptions } from '../../../../../types/order/escrow/IAchAccountData';
import { IAchAccountResponse } from 'types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { useFormikContext } from 'formik';
import { IEscrowFileCompleteData } from 'types/order/escrow/IEscrowFileCompleteData';
import { CreateWireFromACHModal } from './createWireFromACH/CreateWireFromACHModal';
import { createWireFromACHPushAsync } from './createWireFromACH/CreateWireFromACHPushAsync';
import { ICopyACHPushDetails } from './createWireFromACH/interfaces/ICopyACHPushDetails';
import { getISelectOptionsFromIAchAccountResponse } from '../../../../../ui-toolkit/orders/methods/escrow/getAchAccountOptions';
import { useGetBankNameByRoutingNumberQuery } from '../../../../../API/order/escrow/EscrowBaseAPI';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { ePartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { IPartyPerson } from '../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { isBuyerBorrower } from '../../../../../ui-toolkit/orders/interfaces/IParty';

export interface ICreateEditAchPushRowProps {
  achPush: IAchPushDisbursementData;
  index: number;
  onDelete: (index: number) => void;
  onSend: (achPush: IAchPushDisbursementData) => void;
  fieldName: string;
  achAccounts: IAchAccountResponse[];
  parties: IPartyPerson[];
  onAcknowledgeSuccess: () => void;
  escrowFileId: string;
  state: string;
  fetching: boolean;
  setShowDeletingACHPush: (showDelete: boolean) => void;
}

export const CreateEditAchPushRow: React.FC<ICreateEditAchPushRowProps> = ({
  achPush,
  index,
  onDelete,
  onSend,
  fieldName,
  achAccounts,
  parties,
  onAcknowledgeSuccess,
  escrowFileId,
  state,
  fetching,
  setShowDeletingACHPush,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPushDepositsEdit]);
  const formik = useFormikContext<IEscrowFileCompleteData>();
  const isAchAccountPersisted: boolean =
    !!formik.values.achPushDisbursements && !!formik.values.achPushDisbursements[index].account?.id;
  const achPushId = formik.values.achPushDisbursements && formik.values.achPushDisbursements[index].id;
  const hideACHAccountFields: boolean =
    !!formik.values.achPushDisbursements &&
    !!(formik.values.achPushDisbursements[index].account as any)?.id &&
    !formik.values.achPushDisbursements[index].id;
  const isAchPushPersisted: boolean = !!achPushId;
  const disableAchInput = isAchPushPersisted && isAchAccountPersisted;
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const { data: bank } = useGetBankNameByRoutingNumberQuery(achPush.account?.routingNumber ?? '', {
    skip: achPush.account?.routingNumber?.length !== 9,
  });
  const [showCreateWire, setShowCreateWire] = useState<boolean>(false);

  const onCloseConvertToWireModal = () => {
    setShowCreateWire(false);
    setErrorMessages([]);
    setIsSaved(false);
  };
  // parties
  const borrowerParties = parties.filter(isBuyerBorrower);
  const sellerParties = parties.filter((party) => {
    return party.purpose === ePartyPurpose.seller;
  });
  const sellerPartyOptions: ISelectOption[] = sellerParties.map((party) => ({
    label: [party.first_name, party.last_name].join(' '),
    value: party.id,
  }));
  const borrowerPartyOptions: ISelectOption[] = borrowerParties.map((party) => ({
    label: [party.first_name, party.last_name].join(' '),
    value: party.id,
  }));

  const achPushToWire: ICopyACHPushDetails = {
    id: achPush.id,
    ach_account: {
      account_number_last_four: achPush.account?.accountNumber || '',
      id: achPush.account?.id || '',
      name: achPush.account?.name || '',
      routing_number: '',
    },
    amount_cents: achPush.amountCents,
    escrow_file: {
      id: escrowFileId,
    },
    state_abbreviation: state,
  };

  const accounts = useMemo(() => getISelectOptionsFromIAchAccountResponse(achAccounts), [achAccounts]);

  return (
    <>
      <Grid>
        {!achPush.id && (
          <Grid columnCount={2} alignItems={`start`}>
            <SelectField
              name={`${fieldName}.${index}.account`}
              options={accounts}
              label={'Existing ACH Account (Optional)'}
              isClearable
              errorText={''}
              isDisabled={!hasEditPermission}
            />
          </Grid>
        )}
        {!achPush.id && !(achPush.account as any)?.id && <BodyText>Or enter a new one:</BodyText>}
        {!hideACHAccountFields && (
          <Grid templateColumns={'1fr 1fr 1fr 1fr 0.2fr'} alignItems={`center`}>
            <TextField
              name={`${fieldName}.${index}.account.name`}
              label="Account Name"
              placeholder="Samuel L. Jackson"
              readOnly={!hasEditPermission || disableAchInput}
            />
            <TextField
              name={`${fieldName}.${index}.account.routingNumber`}
              label="Routing Number"
              placeholder="999999999"
              readOnly={!hasEditPermission || disableAchInput}
              hintText={bank?.name}
            />
            <TextField
              name={`${fieldName}.${index}.account.accountNumber`}
              label="Account Number"
              placeholder="999999-99"
              readOnly={!hasEditPermission || disableAchInput}
            />
            <SelectField
              name={`${fieldName}.${index}.account.accountType`}
              options={accountTypeOptions}
              label={'Account Type'}
              isDisabled={!hasEditPermission || disableAchInput}
              defaultValue={accountTypeOptions[0]}
            />
          </Grid>
        )}
        <Grid templateColumns={'1fr 1fr 1fr 1fr 0.2fr'} alignItems={`center`}>
          <TextField
            name={`${fieldName}.${index}.wellsFargoTransactionNumber`}
            label="Memo"
            placeholder="StreetAddress-Zip (ex: 123MainSt-90210)"
            readOnly={!hasEditPermission}
          />
          <NumberField
            name={`${fieldName}.${index}.amountCents`}
            label="Amount"
            mode="currency"
            placeholder="123,456.78"
            valueIsCents
            disabled={!hasEditPermission}
          />
          <SelectField
            name={`${fieldName}.${index}.category`}
            options={achDisbursementCategoriesOptions}
            label={'Payee Category'}
            isDisabled={!hasEditPermission}
          />
          <div>
            {formik.values.achPushDisbursements?.[index].category === eACHDisbursementCategories.Borrower && (
              <SelectField
                name={`${fieldName}.${index}.account.ownerId`}
                options={borrowerPartyOptions}
                label={'Borrower'}
                isDisabled={!hasEditPermission}
              />
            )}
            {formik.values.achPushDisbursements?.[index].category === eACHDisbursementCategories.Seller && (
              <SelectField
                name={`${fieldName}.${index}.account.ownerId`}
                options={sellerPartyOptions}
                label={'Seller'}
                isDisabled={!hasEditPermission}
              />
            )}
          </div>
          <div style={{ marginTop: '-70px' }}>
            <FlexRow justifyContent={'start'} verticalAlign={'center'}>
              <Button
                intent={'ghost'}
                isInline
                type={'button'}
                onClick={() => onDelete(index)}
                disabled={!hasEditPermission}
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </FlexRow>
          </div>
        </Grid>
        {isAchPushPersisted && (
          <FlexRow justifyContent={'start'} verticalAlign={'center'}>
            {fetching && <RotatingIcon type={IconType.Loading} />}
            <Button
              intent={'primary'}
              type={'button'}
              onClick={() => onSend(achPush)}
              disabled={!hasEditPermission || !isAchAccountPersisted}
            >
              Send
            </Button>
            <Button intent={'primary'} type={'button'} onClick={() => setShowCreateWire(true)}>
              Create Wire
            </Button>
          </FlexRow>
        )}
      </Grid>
      {showCreateWire && (
        <CreateWireFromACHModal
          achPush={achPushToWire}
          errorMessages={errorMessages}
          isSaved={isSaved}
          onAcknowledgeSuccess={onAcknowledgeSuccess}
          onClose={onCloseConvertToWireModal}
          onDeleteACHPush={() => {
            setShowDeletingACHPush(true);
            onCloseConvertToWireModal();
            onDelete(index);
          }}
          onValidSubmit={async (values) => {
            try {
              await createWireFromACHPushAsync(values);
            } catch (e: any) {
              if (!!e) {
                setErrorMessages([e]);
              }
            }
            setIsSaved(true);
          }}
        />
      )}
    </>
  );
};
